import { BarChart } from '@mui/x-charts/BarChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { chartsGridClasses } from '@mui/x-charts/ChartsGrid';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { HashtagIcon } from '@heroicons/react/20/solid';
import { PercentBadgeIcon } from '@heroicons/react/24/outline';

import { Card } from "../../../../common/Atoms/Card";
import { MultiButtonCardHeader } from "../../../../common/Atoms/MultiButtonCardHeader";
import { useFetchInvoiceHistoryMetricsQuery } from '../../../services/api/organisationApi/organisation';
import { Select } from '../../../../common/Atoms/Select';
import { LoadingOverlay } from '../../Molecules/LoadingOverlay';
import { Button } from '../../../../common/Atoms/Button';

// Legacy palette — still consumed by EmailDeliveryWidget
export const paidOnTimeColor = `#2ADACE`;

export const openColor = `#7FD1C2`;

export const clickColor = `#3C9D8E`;

export const overdueColor = `#467691`;

export const paidLateColor = `#FF5E5C`;

export const outstandingColor = `#34454E`;

// Invoice history palette (design 1e)
const statusStyles = {
  paid: { label: `Paid`, color: `#3fa392`, bg: `#e9f5f2` },
  paidLate: { label: `Paid late`, color: `#e9a23b`, bg: `#fbf2e2` },
  overdue: { label: `Overdue`, color: `#dd6360`, bg: `#fbeceb` },
  outstanding: { label: `Outstanding`, color: `#94a9b8`, bg: `#eef2f5` },
} as const;

const periodOptions = [
  { value: 3, label: `3 months` },
  { value: 6, label: `6 months` },
  { value: 9, label: `9 months` },
  { value: 12, label: `12 months` },
];

export function InvoiceHistory() {
  const { organisationId } = useParams<{ organisationId: string }>();
  const [period, setPeriod] = useState(6);
  // Toggle between 'totals' and 'percentage'
  const [displayMode, setDisplayMode] = useState<`totals` | `percentage`>(`totals`);

  // Only load this dataset to keep queries light
  const { data: metricsResponse, isLoading } = useFetchInvoiceHistoryMetricsQuery(
    { orgId: organisationId, period },
    {
      skip: !organisationId,
      refetchOnMountOrArgChange: true,
    },
  );

  // Original data in totals
  const data = useMemo(() => {
    if (!metricsResponse?.history)
      return [
        {
          paidLate: 0,
          outstanding: 0,
          overdue: 0,
          paid: 0,
          label: `Jan`,
        },
      ];

    return metricsResponse.history.map(item => ({
      ...item,
      paidLate: item.paidLate || 0,
    }));
  }, [metricsResponse]);

  // Totals per status across the whole period, for the summary rail + subtitle
  const totals = useMemo(() => data.reduce(
    (acc, item) => ({
      paid: acc.paid + item.paid,
      paidLate: acc.paidLate + item.paidLate,
      overdue: acc.overdue + item.overdue,
      outstanding: acc.outstanding + item.outstanding,
    }),
    { paid: 0, paidLate: 0, overdue: 0, outstanding: 0 },
  ), [data]);

  const grandTotal = totals.paid + totals.paidLate + totals.overdue + totals.outstanding;

  const upcomingCount = metricsResponse?.history?.find(item => item.isUpcoming)?.outstanding || 0;

  const dueCount = grandTotal - upcomingCount;

  const hasPaidLate = totals.paidLate > 0;

  // Convert totals to percentages when needed
  const displayData = useMemo(() => {
    if (displayMode === `totals`) {
      return data;
    }
    else {
      return data.map(item => {
        const total = item.overdue + item.paid + item.paidLate + item.outstanding;

        return {
          ...item,
          overdue: total > 0 ? (item.overdue / total) * 100 : 0,
          paid: total > 0 ? (item.paid / total) * 100 : 0,
          paidLate: total > 0 ? (item.paidLate / total) * 100 : 0,
          outstanding: total > 0 ? (item.outstanding / total) * 100 : 0,
        };
      });
    }
  }, [data, displayMode]);

  // Use the proper formatter based on display mode
  const seriesData = useMemo(() => {
    const valueFormatter =
      displayMode === `totals`
        ? (value: number | null) => `${value}`
        : (value: number | null) => `${value?.toFixed(0)}%`;

    // Stack order bottom-to-top: paid, paid late, overdue, outstanding (design 1e)
    return [
      { dataKey: `paid`, label: statusStyles.paid.label, valueFormatter, color: statusStyles.paid.color, stack: `total` },
      { dataKey: `paidLate`, label: statusStyles.paidLate.label, valueFormatter, color: statusStyles.paidLate.color, stack: `total` },
      { dataKey: `overdue`, label: statusStyles.overdue.label, valueFormatter, color: statusStyles.overdue.color, stack: `total` },
      { dataKey: `outstanding`, label: statusStyles.outstanding.label, valueFormatter, color: statusStyles.outstanding.color, stack: `total` },
    ].filter(item => item.dataKey !== `paidLate` || hasPaidLate);
  }, [displayMode, hasPaidLate]);

  const railKeys = (Object.keys(statusStyles) as (keyof typeof statusStyles)[])
    .filter(key => key !== `paidLate` || hasPaidLate);

  return (
    <LoadingOverlay loading={ isLoading }
      className={ `h-full` }>
      <Card className={ `h-full min-h-fit` }
        variant={ `soft` }>
        <MultiButtonCardHeader
          title={ `Invoice History` }
          description={ `${dueCount} invoices due in the last ${period} months${upcomingCount ? `, ${upcomingCount} upcoming` : ``}` }
          helpText={ `Invoices are grouped by the month they are due. Paid: paid by the due date. Paid late: paid after the due date. Overdue: unpaid and past the due date. Outstanding: unpaid and not yet due. Upcoming: unpaid invoices due after this month.` }
          variant={ `plain` }
          cta={
            <div className={ `flex space-x-2` }>
              <div className={ `w-32` }>
                <Select
                  options={ periodOptions }
                  onChange={ value => setPeriod(value.value) }
                  selected={ period }
                />
              </div>
              <div>
                <Button
                  className={ `rounded-tr-none rounded-br-none rounded-tl-md rounded-bl-md focus:ring-0` }
                  color={ displayMode === `totals` ? `paidniceGreen` : `paidniceGreenOutline` }
                  onClick={ () => setDisplayMode(`totals`) }
                >
                  <HashtagIcon className={ `w-5 h-5` } />
                </Button>

                <Button
                  className={ `rounded-tl-none rounded-bl-none rounded-tr-md rounded-br-md focus:ring-0` }
                  color={ displayMode === `percentage` ? `paidniceGreen` : `paidniceGreenOutline` }
                  onClick={ () => setDisplayMode(`percentage`) }
                >
                  <PercentBadgeIcon className={ `w-5 h-5` } />
                </Button>
              </div>
            </div>
          }
        />
        <div className={ `mt-4 grid gap-4 md:grid-cols-[116px_1fr] md:gap-5` }>
          <div className={ `grid grid-cols-2 gap-2 md:flex md:flex-col` }>
            {
              railKeys.map(key => (
                <div
                  key={ key }
                  className={ `rounded-r-lg py-1.5 px-2.5` }
                  style={ { borderLeft: `3px solid ${statusStyles[key].color}`, background: statusStyles[key].bg } }
                >
                  <div className={ `text-xs text-gray-500` }>{ statusStyles[key].label }</div>
                  <div className={ `text-lg font-semibold leading-tight` }
                    style={ { color: `#24404e` } }>{ totals[key] }</div>
                </div>
              ))
            }
          </div>
          <BarChart
            dataset={ displayData }
            xAxis={ [{
              scaleType: `band`,
              dataKey: `label`,
              disableTicks: true,
              tickLabelStyle: { fill: `#6b7c87`, fontSize: 12 },
            }] }
            yAxis={ [{ position: `none`, tickNumber: 3 }] }
            series={ seriesData }
            grid={ { horizontal: true } }
            borderRadius={ 5 }
            hideLegend
            height={ 300 }
            margin={ { left: 0, right: 0, top: 8, bottom: 0 } }
            sx={ {
              [`.${chartsGridClasses.line}`]: { stroke: `#eef1f4` },
              [`.${axisClasses.line}`]: { stroke: `#e6eaee` },
            } }
          />
        </div>
      </Card>
    </LoadingOverlay>
  );
}
